import { collection, getDocs, query, where, orderBy, writeBatch, doc, setDoc, Timestamp } from "firebase/firestore";
import { db } from "./firebase";
import { COLLECTIONS } from "./collections";

/**
 * ClassPlanningService
 * Serviço de Planejamento Inteligente de Aulas.
 * 
 * LÓGICA POR OBJETIVOS (não por tópicos):
 * - Cada atividade tem N objetivos, cada objetivo tem M tópicos
 * - A média de um objetivo = média das notas dos seus tópicos (de todos os alunos da turma)
 * - O ciclo semanal distribui 2 OBJETIVOS por semana
 * - Objetivos com tópicos fundamentais têm prioridade
 * - Objetivos com média mais baixa aparecem mais vezes no ciclo
 */

// Helpers
const getISOWeekNumber = (dateStr) => {
    const date = new Date(dateStr + "T00:00:00");
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
};

const getWeekKey = (dateStr) => {
    const date = new Date(dateStr + "T00:00:00");
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
    const year = d.getUTCFullYear();
    const week = getISOWeekNumber(dateStr);
    return `${year}-W${String(week).padStart(2, "0")}`;
};

const ClassPlanningService = {

    /**
     * Calcula a média da turma POR OBJETIVO.
     */
    calculateObjectiveAverages: (evaluations, objectives, levels) => {
        const levelOrderMap = new Map();
        levels.forEach(l => levelOrderMap.set(String(l.id), Number(l.order || 0)));

        const objData = {};
        objectives.forEach(obj => {
            const topicEntries = {};
            (obj.topics || []).forEach(t => {
                topicEntries[t.id] = {
                    id: t.id,
                    name: t.name,
                    isFundamental: !!t.isFundamental,
                    scores: []
                };
            });

            objData[obj.id] = {
                id: obj.id,
                name: obj.name,
                topicCount: (obj.topics || []).length,
                hasFundamental: (obj.topics || []).some(t => !!t.isFundamental),
                topics: topicEntries
            };
        });

        evaluations.forEach(evalDoc => {
            if (!evalDoc?.objectives) return;
            evalDoc.objectives.forEach(evalObj => {
                const objId = String(evalObj.id || evalObj.idObjective);
                if (!objData[objId]) return;

                (evalObj.topics || []).forEach(t => {
                    const topicId = String(t.id || t.idTopic);
                    if (!objData[objId].topics[topicId]) return;
                    const levelValue = levelOrderMap.get(String(t.idLevel)) ?? 0;
                    objData[objId].topics[topicId].scores.push(levelValue);
                });
            });
        });

        const result = {};
        Object.values(objData).forEach(obj => {
            const topicResults = [];
            let totalTopicAvg = 0;
            let topicsWithScores = 0;

            Object.values(obj.topics).forEach(topic => {
                const avg = topic.scores.length > 0
                    ? topic.scores.reduce((a, b) => a + b, 0) / topic.scores.length
                    : 0;
                topicResults.push({
                    id: topic.id,
                    name: topic.name,
                    isFundamental: topic.isFundamental,
                    avg: Math.round(avg * 100) / 100,
                    totalMembers: topic.scores.length
                });
                totalTopicAvg += avg;
                topicsWithScores++;
            });

            const objectiveAvg = topicsWithScores > 0
                ? totalTopicAvg / topicsWithScores
                : 0;

            result[obj.id] = {
                id: obj.id,
                name: obj.name,
                avg: Math.round(objectiveAvg * 100) / 100,
                topicCount: obj.topicCount,
                hasFundamental: obj.hasFundamental,
                topics: topicResults
            };
        });

        return result;
    },

    /**
     * Gera o ciclo semanal de OBJETIVOS (2 por semana).
     */
    generateWeeklyCycle: (objectiveAverages, maxLevelValue, objectivesPerWeek = 2) => {
        const objectives = Object.values(objectiveAverages);
        if (objectives.length === 0) return [];

        if (objectives.length <= objectivesPerWeek) {
            return [objectives.map(o => ({
                id: o.id,
                name: o.name,
                hasFundamental: o.hasFundamental,
                avgScore: o.avg,
                topicCount: o.topicCount,
                topics: o.topics,
                reason: o.hasFundamental ? "fundamental" : "regular"
            }))];
        }

        const sorted = [...objectives]
            .map(o => ({
                ...o,
                effectiveWeight: Math.max(0.1, maxLevelValue - o.avg) * (o.hasFundamental ? 1.5 : 1.0)
            }))
            .sort((a, b) => b.effectiveWeight - a.effectiveWeight);

        const cycle = [];
        for (let i = 0; i < sorted.length; i += objectivesPerWeek) {
            const weekObjectives = sorted.slice(i, i + objectivesPerWeek).map(o => ({
                id: o.id,
                name: o.name,
                hasFundamental: o.hasFundamental,
                avgScore: o.avg,
                topicCount: o.topicCount,
                topics: o.topics,
                reason: o.hasFundamental
                    ? "fundamental"
                    : (o.effectiveWeight > maxLevelValue * 0.5 ? "priority" : "regular")
            }));
            cycle.push(weekObjectives);
        }

        return cycle;
    },

    /**
     * Distribui o ciclo pelas sessões futuras agrupadas por semana ISO.
     */
    distributePlanToSessions: (sessions, cycle, evaluationEventId, memberCount, classAvgOverall) => {
        if (!cycle.length || !sessions.length) return sessions;

        const weekMap = {};
        sessions.forEach(s => {
            const weekKey = getWeekKey(s.sessionDate);
            if (!weekMap[weekKey]) weekMap[weekKey] = [];
            weekMap[weekKey].push(s);
        });

        const sortedWeeks = Object.keys(weekMap).sort();

        const result = [];
        sortedWeeks.forEach((weekKey, weekIndex) => {
            const cyclePosition = weekIndex % cycle.length;
            const weekObjectives = cycle[cyclePosition];

            weekMap[weekKey].forEach(session => {
                result.push({
                    ...session,
                    lessonPlan: {
                        generatedAt: Timestamp.now(),
                        evaluationEventId: evaluationEventId || "",
                        weekKey,
                        cyclePosition: cyclePosition + 1,
                        cycleLength: cycle.length,
                        objectivesPerWeek: weekObjectives.length,
                        objectives: weekObjectives,
                        classMemberCount: memberCount,
                        classAvgOverall: Math.round(classAvgOverall * 100) / 100
                    }
                });
            });
        });

        return result;
    },

    /**
     * Salva o lessonPlan nas sessões via batch update.
     */
    savePlan: async (idTenant, sessionsWithPlan) => {
        if (!idTenant || !sessionsWithPlan.length) return 0;

        const BATCH_LIMIT = 499;
        let totalUpdated = 0;

        for (let i = 0; i < sessionsWithPlan.length; i += BATCH_LIMIT) {
            const chunk = sessionsWithPlan.slice(i, i + BATCH_LIMIT);
            const batch = writeBatch(db);

            chunk.forEach(session => {
                if (!session.id || !session.lessonPlan) return;
                const ref = doc(db, "tenants", idTenant, COLLECTIONS.SESSIONS, session.id);
                batch.update(ref, { lessonPlan: session.lessonPlan });
            });

            await batch.commit();
            totalUpdated += chunk.length;
        }

        return totalUpdated;
    },

    /**
     * Salva o ciclo de planejamento em planningCycles/{groupId}.
     * A Cloud Function generateDailySessions lê este documento para aplicar
     * o plano automaticamente em sessões recém-criadas.
     */
    saveCycle: async (idTenant, classGroupId, cycle, metadata) => {
        const cycleDoc = doc(db, "tenants", idTenant, COLLECTIONS.PLANNING_CYCLES, classGroupId);
        await setDoc(cycleDoc, {
            idTenant,
            groupId: classGroupId,
            cycleLength: cycle.length,
            weeks: cycle.map((weekObjectives, idx) => ({
                position: idx + 1,
                objectives: weekObjectives
            })),
            anchorWeekKey: metadata.anchorWeekKey,
            evaluationEventId: metadata.evaluationEventId || "",
            classMemberCount: metadata.classMemberCount || 0,
            classAvgOverall: Math.round((metadata.classAvgOverall || 0) * 100) / 100,
            generatedAt: Timestamp.now(),
            updatedAt: Timestamp.now()
        });
    },

    /**
     * Verifica se uma sessão precisa de regeneração do plano.
     */
    needsRegeneration: (session, currentEventId) => {
        if (!session.lessonPlan) return true;
        if (!currentEventId) return false;
        return session.lessonPlan.evaluationEventId !== currentEventId;
    },

    /**
     * Orquestra toda a geração do plano para um grupo de turmas.
     */
    generatePlanForGroup: async (idTenant, idActivity, classGroupId, classIds, currentEventId, deps) => {
        const { ActivityService, LevelService, EnrollmentService, EvaluationService } = deps;

        // 1. Buscar atividade
        const allActivities = await ActivityService.getActivities(idTenant);
        const activity = allActivities.find(a => a.id === idActivity);
        if (!activity) throw new Error("Atividade não encontrada.");

        const objectives = (activity.objectives || []).map(obj => ({
            id: obj.id,
            name: obj.name,
            topics: (obj.topics || []).map(t => ({
                id: t.id,
                name: t.name,
                isFundamental: !!t.isFundamental
            }))
        }));

        if (objectives.length === 0) throw new Error("Atividade sem objetivos configurados.");

        // 2. Buscar níveis
        const levels = await LevelService.getLevels(idTenant);
        const maxLevel = levels.reduce((max, l) => (Number(l.order || 0) > Number(max.order || 0) ? l : max), levels[0] || { order: 0 });
        const maxLevelValue = Number(maxLevel.order || 0);

        // 3. Buscar matrículas ativas
        const memberIdsSet = new Set();
        for (const classId of classIds) {
            const enrollments = await EnrollmentService.getEnrolledMembersByClass(idTenant, classId);
            enrollments.forEach(e => {
                if (!memberIdsSet.has(e.id)) memberIdsSet.add(e.id);
            });
        }

        // 4. Buscar última avaliação de cada membro
        const memberIds = Array.from(memberIdsSet);
        let evaluationsMap = {};
        if (memberIds.length > 0) {
            evaluationsMap = await EvaluationService.getLatestByMemberAndActivity(idTenant, idActivity, memberIds);
        }
        const evaluations = Object.values(evaluationsMap);

        // 5. Calcular médias por OBJETIVO
        const objectiveAverages = ClassPlanningService.calculateObjectiveAverages(evaluations, objectives, levels);

        // 6. Média geral
        const avgValues = Object.values(objectiveAverages).map(o => o.avg);
        const classAvgOverall = avgValues.length > 0
            ? avgValues.reduce((a, b) => a + b, 0) / avgValues.length
            : 0;

        // 7. Gerar ciclo semanal
        const cycle = ClassPlanningService.generateWeeklyCycle(objectiveAverages, maxLevelValue, 2);

        // 8. Buscar sessões futuras
        const todayISO = new Date().toISOString().slice(0, 10);
        const allSessions = [];

        for (const classId of classIds) {
            const q = query(
                collection(db, "tenants", idTenant, COLLECTIONS.SESSIONS),
                where("idClass", "==", classId),
                where("sessionDate", ">=", todayISO),
                orderBy("sessionDate", "asc")
            );
            const snap = await getDocs(q);
            snap.docs.forEach(d => allSessions.push({ id: d.id, ...d.data() }));
        }

        if (allSessions.length === 0) return { sessionsUpdated: 0, cycle, objectiveAverages };

        // 9. Distribuir plano
        const sessionsWithPlan = ClassPlanningService.distributePlanToSessions(
            allSessions, cycle, currentEventId, memberIds.length, classAvgOverall
        );

        // 10. Salvar planos nas sessões
        const sessionsUpdated = await ClassPlanningService.savePlan(idTenant, sessionsWithPlan);

        // 11. Salvar ciclo para a Cloud Function (generateDailySessions)
        const firstWeekKey = sessionsWithPlan.length > 0
            ? getWeekKey(sessionsWithPlan[0].sessionDate)
            : getWeekKey(todayISO);

        await ClassPlanningService.saveCycle(idTenant, classGroupId, cycle, {
            anchorWeekKey: firstWeekKey,
            evaluationEventId: currentEventId,
            classMemberCount: memberIds.length,
            classAvgOverall
        });

        return { sessionsUpdated, cycle, objectiveAverages };
    }
};

export default ClassPlanningService;
